/**
 * Binding to the custom MCP server routes (`/api/flux-agent/mcp-servers`). It moves data in and
 * out of the Rust Worker and holds no decisions: URL checks, credential encryption, discovery
 * and per-tool policy are all enforced server-side (`flux_agent/mcp_servers.rs`). Every call
 * throws the server's message on failure.
 *
 * @module apps/web/src/lib/fluxAgentMcpApi
 */

import { request } from '@/lib/api';

/** Whether calling a tool shows a Confirm / Cancel card first (`ask`) or runs at once (`allow`). */
export type McpToolPolicy = 'ask' | 'allow';

/** How a server's credential is sent. */
export type McpAuthKind = 'none' | 'bearer' | 'header';

/** A tool the last discovery found on a server. */
export interface McpTool {
  /** Stored tool id, used to change its policy. */
  id: string;
  /** The server's own tool name. */
  name: string;
  /** Name the model sees, `mcp_<server>_<tool>`. */
  exposedName: string;
  /** Optional human title from the server. */
  title: string | null;
  /** Description from the server, at most 1,024 characters; untrusted text. */
  description: string;
  /** Current policy; `ask` by default. */
  policy: McpToolPolicy;
  /** The server's hints such as `readOnlyHint`; informational only, never trusted. */
  annotations: Partial<
    Record<'readOnlyHint' | 'destructiveHint' | 'idempotentHint' | 'openWorldHint', boolean>
  >;
}

/** A connected MCP server as the API returns it; the credential itself is never returned. */
export interface McpServer {
  /** Server id. */
  id: string;
  /** Display name. */
  name: string;
  /** Fixed short name used in tool names. */
  slug: string;
  /** The `https://` address. */
  url: string;
  /** How the credential is sent. */
  authKind: McpAuthKind;
  /** Custom header name when `authKind` is `header`. */
  authHeaderName: string | null;
  /** Whether a credential is stored. */
  hasCredential: boolean;
  /** Whether Flux Agent is offered this server's tools. */
  enabled: boolean;
  /** Negotiated MCP protocol revision from the last successful discovery. */
  protocolVersion: string | null;
  /** `{name, version}` the server reported. */
  serverInfo: {
    /** Server implementation name reported during discovery. */
    name: string;
    /** Server implementation version reported during discovery. */
    version: string;
  } | null;
  /** True when the server offered more than 50 tools and only the first 50 were kept. */
  toolsTruncated: boolean;
  /** UTC time of the last successful discovery. */
  lastDiscoveredAt: string | null;
  /** Last error message, or a note about skipped tools. */
  lastError: string | null;
  /** UTC time of `lastError`. */
  lastErrorAt: string | null;
  /** Tools from the last discovery, in the server's order. */
  tools: McpTool[];
}

/** A credential to store; omit it on edit to keep the stored one. */
export interface McpAuthInput {
  /** `none` clears the credential. */
  kind: McpAuthKind;
  /** Header name for `header`. */
  headerName?: string;
  /** Token or header value for `bearer` and `header`. */
  value?: string;
}

/** Fields of a create or edit; on edit, omitted fields keep their stored values. */
export interface McpServerInput {
  /** Display name, 1-40 characters. */
  name?: string;
  /** `https://` address. */
  url?: string;
  /** Whether the tools are offered to Flux Agent. */
  enabled?: boolean;
  /** New credential. */
  auth?: McpAuthInput;
}

/** Unwrap a response or throw its message. */
async function call<T>(endpoint: string, init: RequestInit, fallback: string): Promise<T> {
  const response = await request<T>(endpoint, init);
  if (!response.success || response.data === undefined)
    throw new Error(response.error?.message || fallback);
  return response.data;
}

/** Custom MCP server calls. */
export const fluxAgentMcpApi = {
  /** List the caller's servers with their tools. */
  async list(): Promise<McpServer[]> {
    return (
      await call<{ servers: McpServer[] }>(
        '/flux-agent/mcp-servers',
        {},
        'Could not load MCP servers.'
      )
    ).servers;
  },
  /** Add a server; the server runs a first discovery and reports any error as `lastError`. */
  async create(input: McpServerInput): Promise<McpServer> {
    return (
      await call<{ server: McpServer }>(
        '/flux-agent/mcp-servers',
        { method: 'POST', body: JSON.stringify(input) },
        'Could not add the server.'
      )
    ).server;
  },
  /** Edit a server. */
  async update(id: string, input: McpServerInput): Promise<McpServer> {
    return (
      await call<{ server: McpServer }>(
        `/flux-agent/mcp-servers/${encodeURIComponent(id)}`,
        { method: 'PATCH', body: JSON.stringify(input) },
        'Could not save the server.'
      )
    ).server;
  },
  /** Remove a server and its tools. */
  async remove(id: string): Promise<void> {
    await call<{ deleted: boolean }>(
      `/flux-agent/mcp-servers/${encodeURIComponent(id)}`,
      { method: 'DELETE' },
      'Could not remove the server.'
    );
  },
  /** Test the connection and refresh the tool list. */
  async discover(id: string): Promise<McpServer> {
    return (
      await call<{ server: McpServer }>(
        `/flux-agent/mcp-servers/${encodeURIComponent(id)}/discover`,
        { method: 'POST', body: '{}' },
        'Could not reach the server.'
      )
    ).server;
  },
  /** Set one tool to ask first or allowed without asking. */
  async setToolPolicy(id: string, toolId: string, policy: McpToolPolicy): Promise<McpServer> {
    return (
      await call<{ server: McpServer }>(
        `/flux-agent/mcp-servers/${encodeURIComponent(id)}/tools/${encodeURIComponent(toolId)}`,
        { method: 'PUT', body: JSON.stringify({ policy }) },
        'Could not change the tool.'
      )
    ).server;
  },
};
